import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Overline } from "@/components/motion";

const MIST = "/boats-dusk.jpg";

const sessions = [
  ["Name Where You Are", "Name it to tame it. What triggers you, teaches you. Acknowledge your present reality rather than rushing to escape or fix it."],
  ["The Stories We Tell Ourselves", "The good, the bad and the ugly. Identify the narratives that keep you stuck — and intentionally develop healthier, more truthful ones."],
  ["Pain as Sacred Ground", "Learn to welcome and reframe pain rather than simply resist it. Meaning, value and even joy can emerge through difficult experiences."],
  ["The Courage to Stay", "A caterpillar cannot leave the cocoon prematurely without consequences. The value of staying present through transformation."],
  ["Resilience Is Built in Community", "Identify the people who listen, challenge, encourage and still believe in you. Build a consistent circle that sustains growth."],
  ["Leading Yourself First", "Move from “I deserve to lead” to “I get to lead.” Responsibility, intentionality and personal accountability."],
  ["Habits of the Whole Person", "Exchange old patterns for new ones. Explore the whole person — not just productivity or performance."],
  ["Owning Your Story", "Tell parts of your story to trusted people with courage, compassion and humility — rather than letting shame keep you isolated."],
  ["Writing a New Ending", "Imagine a different and beautiful ending. Discover what Version 7.0 can produce when the past stops dictating the entire future."],
  ["Living into the Not Yet", "Live fully even when the outcome is not yet visible. The Not Yet becomes a source of hope rather than frustration."],
];

const toolkit = [
  "5-4-3-2-1 Grounding Method", "Naming where you are", "Identifying triggers",
  "The stories we tell ourselves", "Pain as sacred ground", "The courage to stay",
  "Resilience through community", "Leading yourself first", "Habits of the whole person",
  "Owning your story", "Writing a new ending", "Living into the Not Yet",
  "John Maxwell's 15 Laws of Growth", "The 360 Degree Leader", "Leadership Gold",
  "Everyone Communicates, Few Connect", "Food and Mood", "The Hand-Brain Model",
  "The Art & Heart of Repair Work", "Emotional fluency", "DISC personality assessment",
  "Genogram exercises", "Emotionally Healthy Spirituality", "Johari Window",
];

const steps = [
  ["Reframe", "Tell a truer story about the season you're in."],
  ["Repurpose", "Let pain and disruption become material, not waste."],
  ["Pivot", "Turn deliberately toward what is still possible."],
  ["Grow", "Better, not bitter — a return on failure."],
];

export default function MessyMiddle() {
  useEffect(() => {
    document.title = "The Messy Middle — People Growing People";
  }, []);

  return (
    <main data-testid="messy-middle-page">
      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-end overflow-hidden bg-ink">
        <img
          src={MIST}
          alt="Longtail boats resting on still water under a stormy dusk sky, light breaking on the horizon"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/20" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pb-24 pt-44 w-full">
          <MaskedLine delay={0.15}>
            <span className="text-xs uppercase tracking-[0.3em] text-paper/60">
              The Signature Journey
            </span>
          </MaskedLine>
          <h1 className="font-serif text-paper text-[13vw] sm:text-6xl md:text-8xl leading-[0.95] tracking-tight mt-6">
            <MaskedLine delay={0.3}>The Messy</MaskedLine>
            <MaskedLine delay={0.42}>
              <span className="italic text-clay">Middle.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.6} className="mt-8 max-w-2xl">
            <span className="text-paper/80 text-base md:text-lg leading-relaxed">
              The in-between season — where life has been disrupted, but the next
              chapter has not yet become clear. A 10-session coaching journey
              through it, not around it.
            </span>
          </MaskedLine>
        </div>
      </section>

      {/* WHAT YOU SEE / WHAT'S GROWING */}
      <section className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="md:sticky md:top-32">
              <Reveal>
                <Overline>What You See</Overline>
                <p className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-tight mt-6">
                  Failures. <span className="italic">Fixtures.</span> FOMOs.
                </p>
              </Reveal>
            </div>
          </div>
          <div className="md:col-span-7 md:pl-12 md:border-l md:border-line space-y-8">
            <Reveal>
              <p className="text-base md:text-lg leading-relaxed text-ink/75">
                In the messy middle you may feel chaos, confusion, loss, failure,
                disappointment, denial, dismissal — and a complete lack of visible
                outcomes. It is disorienting, and it is more common than anyone
                admits.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-base md:text-lg leading-relaxed text-ink/75">
                But what you cannot always see is that significant growth may
                already be taking place underneath the surface. Roots before
                branches. This journey helps you pause, acknowledge where you
                really are, and discover what the season is growing in you.
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="font-serif italic text-2xl md:text-3xl text-moss leading-snug">
                The messy parts of our story are not necessarily disqualifiers.
                They can become catalysts.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* METHOD: REFRAME → REPURPOSE → PIVOT → GROW */}
      <section data-testid="method-steps" className="bg-ink text-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <Overline className="text-paper/50">The Movement</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-7xl tracking-tight leading-none mt-6">
              Reframe → Repurpose → <span className="italic text-clay">Pivot → Grow</span>
            </h2>
          </Reveal>
          <div className="mt-16 md:mt-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-paper/15 border border-paper/15">
            {steps.map(([t, c], i) => (
              <Reveal key={t} delay={i * 0.08} className="bg-ink">
                <div className="p-8 md:p-10 min-h-[220px] flex flex-col">
                  <p className="font-serif text-5xl text-paper/20">0{i + 1}</p>
                  <p className="font-serif text-2xl md:text-3xl mt-auto pt-8 tracking-tight">{t}</p>
                  <p className="text-sm text-paper/60 mt-3 leading-relaxed">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 10 SESSIONS */}
      <section data-testid="sessions-section" className="border-t border-line">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <Overline>The Journey</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-none mt-6 max-w-3xl">
              Ten sessions. One honest walk{" "}
              <span className="italic text-moss">through the middle.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-ink/70 max-w-2xl">
              Each session runs 60–90 minutes, individually or in groups —
              combining coaching, facilitation, teaching, reflection, journaling,
              practical exercises, accountability and honest conversation.
            </p>
          </Reveal>
          <div className="mt-16 border-t border-line">
            {sessions.map(([title, copy], i) => (
              <Reveal key={title} delay={Math.min(i * 0.04, 0.3)}>
                <div
                  data-testid={`session-${i + 1}`}
                  className="group grid md:grid-cols-12 gap-4 md:gap-8 py-8 md:py-10 border-b border-line items-baseline hover:bg-sand/50 transition-colors duration-300 px-2 md:px-4 -mx-2 md:-mx-4"
                >
                  <span className="md:col-span-2 font-serif text-3xl md:text-4xl text-clay/70 group-hover:text-clay transition-colors duration-300">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="md:col-span-4 font-serif text-2xl md:text-3xl tracking-tight">
                    {title}
                  </h3>
                  <p className="md:col-span-6 text-sm md:text-base text-ink/70 leading-relaxed">
                    {copy}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TOOLKIT */}
      <section data-testid="toolkit-section" className="border-t border-line bg-sand/40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <Overline>The Toolkit</Overline>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-none mt-6 max-w-3xl">
              Reflective <span className="italic text-clay">and</span> practical.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-ink/70 max-w-2xl">
              This work is not about simply inspiring you. It gives you practical
              handles you can use in real life — grounded in proven frameworks and
              honest, human practice.
            </p>
          </Reveal>
          <div className="mt-14 flex flex-wrap gap-3">
            {toolkit.map((t, i) => (
              <Reveal key={t} delay={Math.min(i * 0.03, 0.4)}>
                <span className="inline-block border border-line bg-paper px-5 py-2.5 text-sm text-ink/80 hover:border-clay hover:text-clay transition-colors duration-300">
                  {t}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-moss text-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-7xl tracking-tight leading-[0.98] max-w-4xl">
              I'm not waiting to become.{" "}
              <span className="italic">I'm becoming in the middle.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 text-base md:text-lg leading-relaxed text-paper/80 max-w-xl">
              You don't have to go through the messy middle alone. Tell me where
              you are — and let's explore what's possible from here.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <Link
              to="/work-with-alex"
              data-testid="messy-middle-cta-work-with-alex"
              className="group mt-12 inline-flex items-center gap-3 bg-paper text-ink text-xs uppercase tracking-[0.22em] px-10 py-5 hover:bg-clay hover:text-paper transition-colors duration-300"
            >
              Work With Alex
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
